import { InputLabels, SelectField } from '../generic/InputField';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Section, { size } from '../generic/Section';

import Accordion from '../generic/Accordion';
import styles from './index.module.scss';
import { useMediaMatch } from '../../utils';

const sections = ['keyFeatures', 'camera', 'audio', 'inputOutput', 'mounts'];

const camelToText = txt => {
  var str = txt.replace(/([A-Z])/g, ' $1');
  return str.charAt(0).toUpperCase() + str.slice(1);
};

function ProductComparisonHeader({ section }) {
  return <h5 className={styles.sectionHeader}>{camelToText(section)}</h5>;
}

function ProductComparisonContent({ products, section, columns }) {
  return (
    <table className={styles.section}>
      <tbody>
        <tr className={styles.label}>
          {products.map((product, index) => {
            if (columns.indexOf(product.slug) === -1) {
              return null;
            }
            return (
              <th key={index}>
                <em>{product.shortHeading}</em>
              </th>
            );
          })}
        </tr>
        <tr key={section}>
          {products.map((product, index) => {
            if (columns.indexOf(product.slug) === -1) {
              return null;
            }
            return (
              <td key={index}>
                <ul>
                  {product[section] &&
                    product[section].map((usp, index) => {
                      return <li key={index}>{usp.heading || usp}</li>;
                    })}
                </ul>
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );
}

function ProductOptions({ products, onColumnSelect, maxColumns }) {
  const options = products.map(product => {
    return {
      value: product.slug,
      displayValue: product.shortHeading || product.heading,
    };
  });

  const fields = options.map((option, index) => {
    if (index > products.length || index >= maxColumns) {
      return null;
    }
    const id = `column-${index}`;
    return (
      <InputLabels key={index} caption={`Select product`} htmlFor={id} className={styles.option}>
        <SelectField
          options={options}
          defaultValue={option.value}
          id={id}
          name={id}
          onChange={event => {
            onColumnSelect(event.target.value, index);
          }}
        />
      </InputLabels>
    );
  });

  return <div className={styles.productOptions}>{fields}</div>;
}

export default function ProductComparison({ products }) {
  let isSmallScreen = useMediaMatch('screen and (max-width: 767px)');
  let currentMaxColumns = isSmallScreen ? 2 : Math.min(products.length, 3);

  const [columns, setColumns] = useState(products.map(product => product.slug).slice(0, currentMaxColumns));
  const [currentProducts, setCurrentProducts] = useState(products.slice(0, currentMaxColumns));

  useEffect(() => {
    switch (currentMaxColumns) {
      case 2:
        if (columns.length === 3) {
          setColumns(columns.slice(0, 2));
        }
        break;

      default:
        if (columns.length === 2) {
          let arr = [...columns];
          arr[2] = products[0].slug;
          setColumns(arr);
        }
        break;
    }
  }, [columns, currentMaxColumns, products]);

  useEffect(() => {
    setCurrentProducts(columns.map(slug => products.find(product => product.slug === slug)));
  }, [products, columns]);

  let accordionElements = useMemo(
    () =>
      sections
        .map(section => {
          const hasUSPs = currentProducts.filter(product => {
            return product[section] && product[section].length >= 0;
          });
          if (hasUSPs.length === 0) {
            return null;
          }
          return {
            key: section,
            listElement: <ProductComparisonHeader section={section} />,
            expandingElement: (
              <ProductComparisonContent key={section} section={section} products={currentProducts} columns={columns} />
            ),
          };
        })
        .filter(Boolean),
    [columns, currentProducts]
  );

  const onColumnSelect = useCallback(
    (value, index) => {
      let arr = [...columns];
      arr[index] = value;
      setColumns(arr);
    },
    [columns]
  );

  return (
    <Section size={size.max} className={styles.productComparison}>
      {products.length > currentMaxColumns && (
        <ProductOptions products={products} onColumnSelect={onColumnSelect} maxColumns={currentMaxColumns} />
      )}
      <Accordion
        elements={accordionElements}
        listElementClassName={styles.listElement}
        multi={true}
        initWith={[accordionElements[0].key]}
      />
    </Section>
  );
}
